import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { CircleHelp, Loader2, MoreVertical, RefreshCw, Sprout, Trash2 } from 'lucide-react';

import {
  enumeratePasDrsForest,
  expandPasDrsForest,
  generatePasPracticeDraftOne,
  getPasDrsForestLatest,
  markPasDrsEntranceExpandFailed,
  type DrsEntranceCandidate,
  type DrsForestView,
  type DrsLeafView,
} from '../../services/pasMathPkpService';
import { knowledgeService, type KnowledgeParticlePapView } from '../../services/knowledgeService';
import { appConfig } from '../../config/appConfig';
import type { KnowledgeParticle } from '../../types/domain';
import {
  createPasPracticeLibraryItem,
  listPasPracticeNegativeExampleQuestions,
  pasPracticeLibraryHasLeaf,
  type PasPracticeLibraryItem,
} from '../../utils/pasPracticeLibrary';
import { MathTextBlock } from '../MathTextBlock';
import {
  KnowledgeParticleQuestionRichBody,
  resolveKnowledgeParticleQaDisplay,
} from '../KnowledgeParticleQaSections';

type PasDrsPanelProps = {
  assetId: string;
  regionId: string | null;
  sourceKpId?: string | null;
  subject?: string | null;
  autoEnumerate?: boolean;
  libraryItems?: PasPracticeLibraryItem[];
  onPromoteLeaves: (items: PasPracticeLibraryItem[]) => boolean | Promise<boolean>;
  onRemoveLibraryItems?: (ids: string[]) => void;
  /** Sync major/minor view scope so the library list matches promotable steps. */
  onLibraryDisplayScope?: (scope: {
    stepGranularity: 'major' | 'minor';
    hiddenLeafIds: string[];
  }) => void;
  onToast?: (message: string) => void;
};

const ENTRANCE_KIND_LABELS: Record<string, string> = {
  structure_intuition: '结构直觉',
  diagram_quantity: '数形结合',
  special_value: '特值代入',
  enumerate_induce: '枚举归纳',
  concept_reduce: '概念化归',
};

const PATH_FAIL_HINT = '路径入口失败：这条思路求解下去没有拿到解。';

function kindLabelOf(candidate: DrsEntranceCandidate): string {
  return ENTRANCE_KIND_LABELS[candidate.entranceKind] || candidate.label;
}

function statusLabel(status?: string): string {
  if (status === 'sprouted') return '起手方式';
  if (status === 'barren') return '不可用';
  return '待确认';
}

function transitionOpLabel(op?: string): string {
  if (op === 'reduction') return '化归';
  if (op === 'representation_change') return '换表征';
  if (op === 'induction') return '归纳';
  if (op === 'inference') return '推理';
  if (op === 'construction') return '构造';
  if (op === 'execution') return '执行';
  if (op === 'verification') return '验算';
  return op || '下一步';
}

function practiceKindLabel(kind?: string): string {
  if (kind === 'entrance') return '起步';
  if (kind === 'transition') return '台阶';
  return '练习';
}

function formatDrsStepQuestion(question: string): string {
  return String(question || '')
    .trim()
    .replace(/【变式\s*\d+】/g, '')
    .replace(/【台阶\s*\d+】/g, '')
    .trim();
}

type DisplayColumnStep = ColumnStep & {
  proximityLevel: number;
};

type DrsPathView = DrsForestView['paths'][number];
type DrsTransitionView = DrsPathView['transitions'][number];

type ColumnStep = {
  key: string;
  stepNumber: number;
  op?: string;
  summary?: string;
  leaf: DrsLeafView | null;
  /** Major view: all practice leaves merged into this big step (incl. regenerated siblings). */
  chunkLeaves?: DrsLeafView[];
  minorCount?: number;
  minorRange?: string;
};

type StepGranularity = 'major' | 'minor';

const MAJOR_STEP_TARGET = 4;

function leafFromPracticePoint(
  point: NonNullable<DrsTransitionView['practicePoints']>[number],
  transitionId: string,
  pathId?: string,
  entranceKind?: string,
): DrsLeafView {
  return {
    id: point.id,
    question: point.question,
    answer: point.answer,
    hatchPrompt: point.hatchPrompt,
    practiceKind: (point.practiceKind as DrsLeafView['practiceKind']) || 'transition',
    pathId,
    transitionId,
    entranceKind,
    treeRole: 'leaf',
  };
}

function buildColumnSteps(
  path: DrsPathView | null | undefined,
  columnLeaves: DrsLeafView[],
): ColumnStep[] {
  if (!path?.transitions?.length) {
    return columnLeaves.map((leaf, index) => ({
      key: leaf.id,
      stepNumber: index + 1,
      leaf,
    }));
  }

  const steps: ColumnStep[] = [];
  const usedLeafIds = new Set<string>();
  let stepNumber = 0;

  for (const transition of path.transitions) {
    const matchedLeaves = columnLeaves.filter((leaf) => leaf.transitionId === transition.id);
    if (matchedLeaves.length > 0) {
      for (const leaf of matchedLeaves) {
        stepNumber += 1;
        usedLeafIds.add(leaf.id);
        steps.push({
          key: leaf.id,
          stepNumber,
          op: transition.op,
          summary: transition.summary,
          leaf,
        });
      }
      continue;
    }

    const inlinePoint = transition.practicePoints?.find((point) => String(point.question || '').trim());
    stepNumber += 1;
    steps.push({
      key: transition.id,
      stepNumber,
      op: transition.op,
      summary: transition.summary,
      leaf: inlinePoint
        ? leafFromPracticePoint(inlinePoint, transition.id, path.id, path.entranceKind)
        : null,
    });
  }

  for (const leaf of columnLeaves) {
    if (usedLeafIds.has(leaf.id)) continue;
    stepNumber += 1;
    steps.push({
      key: leaf.id,
      stepNumber,
      leaf,
    });
  }

  return steps;
}

function collapseToMajorSteps(steps: ColumnStep[]): ColumnStep[] {
  if (steps.length <= 3) {
    return steps.map((step, index) => ({
      ...step,
      stepNumber: index + 1,
      key: step.key.startsWith('major-') ? step.key : `major-${step.key}`,
    }));
  }

  const targetMajor = Math.min(MAJOR_STEP_TARGET, Math.max(2, Math.ceil(steps.length / 3)));
  const chunkSize = Math.ceil(steps.length / targetMajor);
  const majors: ColumnStep[] = [];

  for (let start = 0; start < steps.length; start += chunkSize) {
    const chunk = steps.slice(start, start + chunkSize);
    const chunkLeaves = chunk
      .map((item) => item.leaf)
      .filter((item): item is DrsLeafView => Boolean(item && String(item.question || '').trim()));
    const anchor = chunk[chunk.length - 1]!;
    majors.push({
      ...anchor,
      key: `major-${anchor.key}`,
      stepNumber: majors.length + 1,
      leaf: chunkLeaves[chunkLeaves.length - 1] ?? anchor.leaf,
      chunkLeaves: chunkLeaves.length > 1 ? chunkLeaves : undefined,
      minorCount: chunk.length,
      minorRange: chunk.length > 1
        ? `含 ${chunk.length} 道更细的台阶`
        : undefined,
    });
  }

  return majors;
}

function resolveColumnSteps(
  path: DrsPathView | null | undefined,
  columnLeaves: DrsLeafView[],
  granularity: StepGranularity,
): ColumnStep[] {
  const allSteps = buildColumnSteps(path, columnLeaves);
  if (granularity === 'minor') return allSteps;
  return collapseToMajorSteps(allSteps);
}

/** In major view, minor steps folded into a big step must not display or promote separately. */
function collectMinorLeavesHiddenInMajorView(
  path: DrsPathView | null | undefined,
  columnLeaves: DrsLeafView[],
): Set<string> {
  const allSteps = buildColumnSteps(path, columnLeaves);
  if (allSteps.length <= 3) return new Set();

  const hidden = new Set<string>();
  const targetMajor = Math.min(MAJOR_STEP_TARGET, Math.max(2, Math.ceil(allSteps.length / 3)));
  const chunkSize = Math.ceil(allSteps.length / targetMajor);
  for (let start = 0; start < allSteps.length; start += chunkSize) {
    const chunk = allSteps.slice(start, start + chunkSize);
    if (chunk.length <= 1) continue;
    const anchorId = chunk[chunk.length - 1]?.leaf?.id;
    for (const step of chunk) {
      const id = step.leaf?.id;
      if (id && id !== anchorId) hidden.add(id);
    }
  }
  return hidden;
}

function proximityCaption(level: number): string {
  if (level >= 5) return '贴近原题';
  if (level >= 4) return '很接近';
  if (level >= 3) return '较近';
  if (level >= 2) return '过渡';
  return '远离原题';
}

function enrichStepsForDisplay(steps: ColumnStep[]): DisplayColumnStep[] {
  const total = steps.length;
  if (total === 0) return [];
  return [...steps].reverse().map((step, index) => ({
    ...step,
    proximityLevel: total === 1
      ? 5
      : Math.max(1, Math.min(5, Math.round(((total - index) / total) * 5) || 1)),
  }));
}

const DIFFICULTY_METER_HELP_TEXT =
  '表示此题与原题的难度相似度。色条越满，越贴近原题、难度越高；色条越短，离原题越远、难度越低。整列自上而下单调变易。';

function StepDifficultyRail({ level }: { level: number }) {
  const clamped = Math.max(1, Math.min(5, level));
  const opacity = 0.18 + (clamped / 5) * 0.72;
  return (
    <div
      className="w-1 shrink-0 self-stretch rounded-full bg-primary-strong"
      style={{ opacity }}
      aria-hidden
    />
  );
}

function DifficultyMeter({ level }: { level: number }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const clamped = Math.max(1, Math.min(5, level));
  return (
    <div className="relative flex items-center gap-1">
      <span className="shrink-0 text-[10px] font-medium text-muted">难度</span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setHelpOpen((open) => !open);
        }}
        className="inline-flex rounded-full text-muted transition hover:text-ink"
        aria-label="难度说明"
        aria-expanded={helpOpen}
      >
        <CircleHelp size={12} strokeWidth={2.2} />
      </button>
      <div className="ml-0.5 flex gap-0.5" aria-hidden>
        {[1, 2, 3, 4, 5].map((slot) => (
          <span
            key={slot}
            className={
              'h-1.5 rounded-full '
              + (slot <= clamped ? 'w-3 bg-primary-strong' : 'w-2 bg-border')
            }
          />
        ))}
      </div>
      {helpOpen ? (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-20 mt-1 w-52 rounded-lg border border-border bg-surface px-2.5 py-2 text-[10px] leading-relaxed text-muted shadow-card"
          onClick={(event) => event.stopPropagation()}
        >
          {DIFFICULTY_METER_HELP_TEXT}
        </div>
      ) : null}
    </div>
  );
}

function columnHasPracticeSteps(columnSteps: ColumnStep[]): boolean {
  return columnSteps.some((step) => Boolean(String(step.leaf?.question || '').trim()));
}

function expandColumnActionLabel(columnSteps: ColumnStep[]): string {
  return columnHasPracticeSteps(columnSteps)
    ? '难度依然太大，展开台阶'
    : '难度太大，展开台阶';
}

function expandColumnActionHint(columnSteps: ColumnStep[]): string {
  if (columnHasPracticeSteps(columnSteps)) {
    return '在下方补上更简单的过渡题，让爬升更缓';
  }
  return '沿此起手方式，生成由易到难的台阶练习';
}

function expandColumnLoadingLabel(columnSteps: ColumnStep[]): string {
  if (columnHasPracticeSteps(columnSteps)) {
    return '正在补上更简单的过渡题…';
  }
  return '正在生成台阶练习…';
}

function candidateMeta(candidate: DrsEntranceCandidate): string {
  return [
    candidate.seededFromPap ? '原题起手' : '',
    candidate.isSourceParticleMethod ? '原解法' : '',
    candidate.matchesPasAttempt ? '卷上用过' : '',
    candidate.alreadyGenerated ? '已有练习' : '',
  ].filter(Boolean).join(' · ');
}

function leavesForColumn(
  leaves: DrsLeafView[],
  candidate: DrsEntranceCandidate,
  pathId?: string,
): DrsLeafView[] {
  return leaves.filter((leaf) => {
    if (!String(leaf.question || '').trim()) return false;
    if (pathId && leaf.pathId === pathId) return true;
    if (!leaf.pathId && leaf.entranceKind === candidate.entranceKind) return true;
    return false;
  });
}

function forestNeedsEnumerate(forest: DrsForestView | null | undefined): boolean {
  if (!forest) return true;
  if (!(forest.candidateEntrances?.length)) return true;
  if (forestHasPapBlueprint(forest)) return false;
  return !(forest.paths?.length);
}

function forestHasPapBlueprint(forest: DrsForestView | null | undefined): boolean {
  const steps = forest?.evidencePack?.papImplicitSteps;
  return Array.isArray(steps) && steps.length > 0;
}

function entranceHasGeneratedPath(forest: DrsForestView, entranceIndex: number): boolean {
  const generated = (forest.paths || []).find(
    (item) => item.entranceIndex === entranceIndex && item.source !== 'pap_blueprint',
  );
  return Boolean(generated && (generated.transitions?.length ?? 0) > 0);
}

function pickSproutedEntrancesNeedingExpand(forest: DrsForestView): DrsEntranceCandidate[] {
  if (!forestHasPapBlueprint(forest)) return [];
  return forest.candidateEntrances
    .filter((candidate) => (
      candidate.status === 'sprouted'
      && (candidate.seededFromPap || candidate.isSourceParticleMethod)
      && !entranceHasGeneratedPath(forest, candidate.index)
      // Already recorded expand failure — do not auto-retry / wipe the marker.
      && !String(candidate.expandError || '').trim()
    ))
    .sort((left, right) => {
      const score = (candidate: DrsEntranceCandidate) => (
        (candidate.seededFromPap ? 2 : 0) + (candidate.isSourceParticleMethod ? 1 : 0)
      );
      return score(right) - score(left);
    });
}

function stampEntranceExpandError(
  forest: DrsForestView,
  entranceIndex: number,
  message: string,
): DrsForestView {
  const hint = String(message || '').trim() || PATH_FAIL_HINT;
  return {
    ...forest,
    candidateEntrances: (forest.candidateEntrances || []).map((item) => (
      item.index === entranceIndex
        ? { ...item, expandError: hint }
        : item
    )),
  };
}

function clearEntranceExpandError(
  forest: DrsForestView,
  entranceIndex: number,
): DrsForestView {
  return {
    ...forest,
    candidateEntrances: (forest.candidateEntrances || []).map((item) => (
      item.index === entranceIndex
        ? { ...item, expandError: null }
        : item
    )),
  };
}

function collectEntranceExpandErrors(forest: DrsForestView | null | undefined): Record<number, string> {
  const errors: Record<number, string> = {};
  for (const candidate of forest?.candidateEntrances || []) {
    const hint = String(candidate.expandError || '').trim();
    if (hint) errors[candidate.index] = hint;
  }
  return errors;
}

function sproutedEntranceFocusIndex(candidates: DrsEntranceCandidate[]): number {
  const index = candidates.findIndex(
    (candidate) => candidate.status === 'sprouted'
      && (candidate.seededFromPap || candidate.isSourceParticleMethod),
  );
  return index >= 0 ? index : 0;
}

function resolveForestKpId(forest: DrsForestView | null | undefined, fallback?: string | null): string {
  return String(forest?.sourceKpId || forest?.evidencePack?.sourceKpId || fallback || '').trim();
}

function columnDisplayPath(
  paths: DrsPathView[],
  entranceIndex: number,
  showPapAfterExpand: boolean,
): DrsPathView | null {
  const matching = paths.filter((item) => item.entranceIndex === entranceIndex);
  const generated = matching.find((item) => item.source !== 'pap_blueprint');
  if (generated) return generated;
  if (showPapAfterExpand) {
    return matching.find((item) => item.source === 'pap_blueprint') ?? null;
  }
  return null;
}

function mergeForestColumn(
  current: DrsForestView,
  incoming: DrsForestView,
  entranceIndex: number,
): DrsForestView {
  const incomingPaths = (incoming.paths || []).filter((item) => item.entranceIndex === entranceIndex);
  const points = new Map<string, NonNullable<DrsForestView['entrancePracticePoints']>[number]>();
  for (const item of current.entrancePracticePoints || []) {
    if (item?.id) points.set(item.id, item);
  }
  for (const item of incoming.entrancePracticePoints || []) {
    if (item?.id) points.set(item.id, item);
  }
  return {
    ...current,
    status: incoming.status || current.status,
    reasonMarkdown: incoming.reasonMarkdown ?? current.reasonMarkdown,
    error: incoming.error ?? current.error,
    paths: [
      ...(current.paths || []).filter((item) => item.entranceIndex !== entranceIndex),
      ...incomingPaths,
    ],
    candidateEntrances: (current.candidateEntrances || []).map((item) => {
      if (item.index !== entranceIndex) return item;
      return incoming.candidateEntrances.find((candidate) => candidate.index === entranceIndex) || item;
    }),
    selectedEntranceIndexes: Array.from(new Set([
      ...(current.selectedEntranceIndexes || []),
      ...(incoming.selectedEntranceIndexes || []),
    ])),
    entrancePracticePoints: Array.from(points.values()),
  };
}

function mergeLeavesForColumn(
  current: DrsLeafView[],
  incoming: DrsLeafView[],
  forest: DrsForestView,
  entranceIndex: number,
): DrsLeafView[] {
  const kind = forest.candidateEntrances.find((item) => item.index === entranceIndex)?.entranceKind;
  const pathIds = new Set(
    (forest.paths || []).filter((item) => item.entranceIndex === entranceIndex).map((item) => item.id),
  );
  const keep = current.filter((leaf) => {
    if (leaf.pathId && pathIds.has(leaf.pathId)) return false;
    if (kind && leaf.entranceKind === kind) return false;
    return true;
  });
  const seen = new Set(keep.map((leaf) => leaf.id).filter(Boolean));
  const add = incoming.filter((leaf) => (
    Boolean(leaf.id)
    && !seen.has(leaf.id)
    && ((leaf.pathId && pathIds.has(leaf.pathId)) || (kind && leaf.entranceKind === kind))
  ));
  return [...keep, ...add];
}

const PAP_STILL_RUNNING_HINT = '原题隐性能力仍在提取（可能需要数分钟）。请稍后再点「列出起手方式」。';
const PAP_EMPTY_AFTER_EXTRACT_HINT = '原题隐性能力提取完成，但没有可用步骤。可稍后再点「列出起手方式」。';

function papViewHasSteps(view: KnowledgeParticlePapView | null | undefined): boolean {
  const projection = view?.pap?.implicitAbilityProjection;
  const steps = projection && typeof projection === 'object' && Array.isArray((projection as { steps?: unknown }).steps)
    ? (projection as { steps: Array<Record<string, unknown>> }).steps
    : [];
  return steps.some((step) => (
    String(step.implicitAbility || '').trim()
    || String(step.reasoning || '').trim()
    || String(step.actionObject || '').trim()
  ));
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForPapBlueprint(particleId: string, kickoff: () => Promise<void>) {
  // Prefer existing PAP — do not force re-extract on every entry.
  const existing = await knowledgeService.getParticlePap(particleId, { kickoff: false }).catch(() => null);
  if (papViewHasSteps(existing)) {
    return { kind: 'ready' as const, view: existing };
  }

  await kickoff();
  const timeoutMs = Number(appConfig.papExtractTimeoutMs) || 600_000;
  const pollMs = 2500;
  const graceMs = 20_000;
  const started = Date.now();
  const deadline = started + timeoutMs;
  let sawRunning = false;
  let retried = false;
  while (Date.now() < deadline) {
    const view = await knowledgeService.getParticlePap(particleId, { kickoff: false }).catch(() => null);
    const status = view?.extractionStatus;
    if (status === 'running' || status === 'pending') sawRunning = true;
    if (status === 'failed') return { kind: 'failed' as const, view };
    if (papViewHasSteps(view)) return { kind: 'ready' as const, view };
    if (sawRunning && status !== 'running' && status !== 'pending') {
      return { kind: 'empty' as const, view };
    }
    if (!sawRunning && !retried && Date.now() - started > graceMs) {
      retried = true;
      await kickoff();
    }
    await sleep(pollMs);
  }
  const view = await knowledgeService.getParticlePap(particleId, { kickoff: false }).catch(() => null);
  const stillRunning = view?.extractionStatus === 'running' || view?.extractionStatus === 'pending';
  return { kind: stillRunning ? 'timeout' as const : 'empty' as const, view };
}

function listActionLabel(loading: 'idle' | 'enumerate' | 'pap' | 'assign'): string {
  if (loading === 'pap') return '正在提取原题隐性能力…';
  if (loading === 'assign') return '正在标出原题起手点…';
  return '正在列出起手方式…';
}

function displayForestReason(reason?: string | null): string {
  const raw = String(reason || '').replace(/下手方式/g, '起手方式').trim();
  if (!raw) return '';
  if (/PAP|原题起手点|相近|已将原题/.test(raw)) {
    return '已列出相近起手；其余路径点「难度太大，展开台阶」。';
  }
  if (/继续展开思路|展开台阶/.test(raw)) {
    return '点某一列「难度太大，展开台阶」生成练习。';
  }
  return raw;
}

const PAP_BACKGROUND_HINT = '提取可能需要几分钟。可以离开本页去处理其他实战答题纸，提取会在后台继续；回来后再点「列出起手方式」。';

export function PasDrsPanel({
  assetId,
  regionId,
  sourceKpId,
  subject,
  autoEnumerate = false,
  libraryItems = [],
  onPromoteLeaves,
  onRemoveLibraryItems,
  onLibraryDisplayScope,
  onToast,
}: PasDrsPanelProps) {
  const [loading, setLoading] = useState<'idle' | 'enumerate' | 'pap' | 'assign'>('idle');
  const [forest, setForest] = useState<DrsForestView | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [leaves, setLeaves] = useState<DrsLeafView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [expandingIndexes, setExpandingIndexes] = useState<Record<number, true>>({});
  const [columnErrors, setColumnErrors] = useState<Record<number, string>>({});
  const [previewLeaf, setPreviewLeaf] = useState<DrsLeafView | null>(null);
  const [previewProximityLevel, setPreviewProximityLevel] = useState(0);
  const [stepGranularity, setStepGranularity] = useState<StepGranularity>('major');
  const [kpSourceParticle, setKpSourceParticle] = useState<KnowledgeParticle | null>(null);
  const [kpSourceLoading, setKpSourceLoading] = useState(false);
  const [stepMenuLeafId, setStepMenuLeafId] = useState<string | null>(null);
  const [regeneratingLeafIds, setRegeneratingLeafIds] = useState<Record<string, true>>({});
  const [removedLeafIds, setRemovedLeafIds] = useState<Record<string, true>>({});
  const [leafProximityOverrides, setLeafProximityOverrides] = useState<Record<string, number>>({});
  const [promoting, setPromoting] = useState(false);
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  const autoExpandedEntrancesRef = useRef<Set<string>>(new Set());
  const runExpandColumnRef = useRef<(entranceIndex: number, options?: { silent?: boolean }) => Promise<boolean>>(
    async () => false,
  );

  const candidates = forest?.candidateEntrances || [];

  const applyForest = (next: DrsForestView) => {
    const sproutedIndexes = next.candidateEntrances
      .filter((item) => item.status === 'sprouted')
      .map((item) => item.index);
    const selectedIndexes = next.selectedEntranceIndexes?.length
      ? next.selectedEntranceIndexes
      : sproutedIndexes;
    const normalized: DrsForestView = {
      ...next,
      selectedEntranceIndexes: selectedIndexes,
    };
    setForest(normalized);
    setSelected(selectedIndexes);
    setFocusIndex(sproutedEntranceFocusIndex(normalized.candidateEntrances));
    // Restore persisted expand failures from forest (not only ephemeral columnErrors).
    setColumnErrors(collectEntranceExpandErrors(normalized));
  };

  const performEnumerate = async (isCancelled?: () => boolean) => {
    const result = await enumeratePasDrsForest({
      assetId,
      regionId,
      sourceKpId,
      subject,
    });
    if (isCancelled?.()) return;
    if (!result.success || !result.forest) {
      setError(result.message || result.error || '分析起手方式失败');
      return;
    }
    applyForest(result.forest);
    if (forestHasPapBlueprint(result.forest)) {
      if (pickSproutedEntrancesNeedingExpand(result.forest).length > 0) {
        onToastRef.current?.('已标出原题起手，正在展开阶梯练习…');
      } else {
        onToastRef.current?.('已标出原题起手点，其余等你点开再展开');
      }
      return;
    }
    const kpId = resolveForestKpId(result.forest, sourceKpId);
    if (!kpId) {
      setError('没有关联知识颗粒，无法提取原题隐性能力。请先绑定题目颗粒。');
      return;
    }
    setLoading('pap');
    try {
      const outcome = await waitForPapBlueprint(kpId, async () => {
        // Only kick extract when PAP is missing; avoid force-rerun on re-entry.
        await knowledgeService.extractParticlePap(kpId, { force: false, wait: false }).catch(() => undefined);
      });
      if (isCancelled?.()) return;
      if (outcome.kind === 'failed') {
        setError(outcome.view?.failure?.message || '原题隐性能力提取失败');
        return;
      }
      if (outcome.kind === 'timeout') {
        setError(PAP_STILL_RUNNING_HINT);
        return;
      }
      if (outcome.kind !== 'ready') {
        setError(PAP_EMPTY_AFTER_EXTRACT_HINT);
        return;
      }
      setLoading('assign');
      const again = await enumeratePasDrsForest({
        assetId,
        regionId,
        sourceKpId: kpId,
        subject,
      });
      if (isCancelled?.()) return;
      if (!again.success || !again.forest) {
        setError(again.message || again.error || '列出起手方式失败');
        return;
      }
      applyForest(again.forest);
      if (!forestHasPapBlueprint(again.forest)) {
        setError(PAP_EMPTY_AFTER_EXTRACT_HINT);
        return;
      }
      if (pickSproutedEntrancesNeedingExpand(again.forest).length > 0) {
        onToastRef.current?.('已标出原题起手，正在展开阶梯练习…');
      } else {
        onToastRef.current?.('已标出原题起手点，其余等你点开再展开');
      }
    } catch (extractError) {
      if (isCancelled?.()) return;
      setError(extractError instanceof Error ? extractError.message : '提取原题隐性能力失败');
    }
  };

  useEffect(() => {
    autoExpandedEntrancesRef.current = new Set();
  }, [assetId, regionId, subject]);

  useEffect(() => {
    if (!stepMenuLeafId) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-drs-step-menu]')) return;
      setStepMenuLeafId(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [stepMenuLeafId]);

  useEffect(() => {
    let cancelled = false;
    if (!assetId) return undefined;

    void (async () => {
      const latest = await getPasDrsForestLatest({ assetId, subject }).catch(() => ({ forest: null, leaves: [] }));
      if (cancelled) return;
      if (latest.forest) {
        applyForest(latest.forest);
        setLeaves(latest.leaves || []);
      }

      // Only enumerate when forest is missing / incomplete.
      // Never wipe an already-expanded ladder just because the drawer opened.
      if (!forestNeedsEnumerate(latest.forest)) return;

      setLoading('enumerate');
      setError(null);
      try {
        await performEnumerate(() => cancelled);
      } finally {
        if (!cancelled) setLoading('idle');
      }
    })();

    return () => { cancelled = true; };
    // enumerate inputs only; performEnumerate closes over them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, autoEnumerate, regionId, sourceKpId, subject]);

  useEffect(() => {
    const kpId = String(sourceKpId || resolveForestKpId(forest, sourceKpId) || '').trim();
    if (!kpId) {
      setKpSourceParticle(null);
      setKpSourceLoading(false);
      return undefined;
    }
    let cancelled = false;
    setKpSourceLoading(true);
    void knowledgeService.getKnowledgeParticle(kpId)
      .then((particle) => {
        if (!cancelled) setKpSourceParticle(particle);
      })
      .catch(() => {
        if (!cancelled) setKpSourceParticle(null);
      })
      .finally(() => {
        if (!cancelled) setKpSourceLoading(false);
      });
    return () => { cancelled = true; };
  }, [forest, sourceKpId]);

  const runEnumerate = async () => {
    if (!assetId || loading !== 'idle' || Object.keys(expandingIndexes).length > 0) return;
    flushSync(() => {
      setLoading('enumerate');
      setError(null);
    });
    try {
      await performEnumerate();
    } finally {
      setLoading('idle');
    }
  };

  const toggleIndex = (index: number) => {
    setSelected((prev) => (
      prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]
    ));
  };

  const runExpandColumn = async (entranceIndex: number, options?: { silent?: boolean }): Promise<boolean> => {
    if (!forest?.id || loading !== 'idle' || expandingIndexes[entranceIndex] || !forestHasPapBlueprint(forest)) return false;
    const forestId = forest.id;
    setExpandingIndexes((prev) => ({ ...prev, [entranceIndex]: true }));
    setError(null);
    setColumnErrors((prev) => {
      const next = { ...prev };
      delete next[entranceIndex];
      return next;
    });
    setForest((current) => (
      current && current.id === forestId
        ? clearEntranceExpandError(current, entranceIndex)
        : current
    ));

    const persistExpandFailure = async (hint: string) => {
      const marked = await markPasDrsEntranceExpandFailed({
        forestId,
        entranceIndex,
        message: hint,
        subject,
      });
      if (marked.forest) {
        setForest((current) => {
          if (!current || current.id !== forestId) return current;
          return mergeForestColumn(current, marked.forest!, entranceIndex);
        });
      } else {
        setForest((current) => (
          current && current.id === forestId
            ? stampEntranceExpandError(current, entranceIndex, hint)
            : current
        ));
      }
      setColumnErrors((prev) => ({ ...prev, [entranceIndex]: hint }));
    };

    const result = await expandPasDrsForest({
      forestId,
      selectedEntranceIndexes: [entranceIndex],
      subject,
      assetId,
    });
    setExpandingIndexes((prev) => {
      const next = { ...prev };
      delete next[entranceIndex];
      return next;
    });
    if (!result.forest) {
      await persistExpandFailure(result.message || PATH_FAIL_HINT);
      return false;
    }
    let nextForest: DrsForestView | null = null;
    setForest((current) => {
      if (!current || current.id !== forestId) return current;
      nextForest = mergeForestColumn(current, result.forest!, entranceIndex);
      return nextForest;
    });
    if (!nextForest) return false;
    const mergedForest = nextForest as DrsForestView;
    setLeaves((current) => mergeLeavesForColumn(current, result.leaves, mergedForest, entranceIndex));
    const candidate = mergedForest.candidateEntrances.find((item) => item.index === entranceIndex);
    const expanded = (mergedForest.selectedEntranceIndexes || []).includes(entranceIndex);
    const path = columnDisplayPath(mergedForest.paths || [], entranceIndex, expanded);
    const failed = Boolean(candidate?.expandError) || !path || (path.transitions?.length ?? 0) === 0;
    if (failed) {
      const hint = candidate?.expandError || PATH_FAIL_HINT;
      // Server expand already persists expandError when FC returns empty;
      // still ensure local + API marker if missing.
      if (!candidate?.expandError) {
        await persistExpandFailure(hint);
      } else {
        setColumnErrors((prev) => ({ ...prev, [entranceIndex]: hint }));
      }
      return false;
    }
    if (result.autoPromote) promote(result.leaves);
    else if (!options?.silent) onToast?.('这条思路已往下展开');
    return true;
  };
  runExpandColumnRef.current = runExpandColumn;

  useEffect(() => {
    if (!forest?.id || loading !== 'idle' || Object.keys(expandingIndexes).length > 0) return;
    const targets = pickSproutedEntrancesNeedingExpand(forest);
    if (!targets.length) return;

    const pending = targets.filter(
      (candidate) => !autoExpandedEntrancesRef.current.has(`${forest.id}:${candidate.index}`),
    );
    if (!pending.length) return;

    const focusCol = candidates.findIndex((candidate) => (
      pending.some((target) => target.index === candidate.index)
    ));
    if (focusCol >= 0) setFocusIndex(focusCol);

    void (async () => {
      let expandedAny = false;
      for (const candidate of pending) {
        autoExpandedEntrancesRef.current.add(`${forest.id}:${candidate.index}`);
        const ok = await runExpandColumnRef.current(candidate.index, { silent: true });
        expandedAny = expandedAny || ok;
      }
      if (expandedAny) onToastRef.current?.('原题起手阶梯已展开');
    })();
  }, [candidates, expandingIndexes, forest, loading]);

  const columns = useMemo(() => {
    const paths = forest?.paths || [];
    const expandedIndexes = forest?.selectedEntranceIndexes?.length
      ? forest.selectedEntranceIndexes
      : selected;
    return candidates.map((candidate) => {
      const expanded = expandedIndexes.includes(candidate.index);
      const path = columnDisplayPath(paths, candidate.index, expanded);
      return {
        candidate,
        path,
        leaves: leavesForColumn(leaves, candidate, path?.id).filter((leaf) => !removedLeafIds[leaf.id]),
      };
    });
  }, [candidates, forest?.paths, forest?.selectedEntranceIndexes, leaves, removedLeafIds, selected]);

  const hiddenMinorLeafIds = useMemo(() => {
    if (stepGranularity !== 'major') return new Set<string>();
    const hidden = new Set<string>();
    for (const { path, leaves: columnLeaves } of columns) {
      for (const id of collectMinorLeavesHiddenInMajorView(path, columnLeaves)) {
        hidden.add(id);
      }
    }
    return hidden;
  }, [columns, stepGranularity]);

  useEffect(() => {
    onLibraryDisplayScope?.({
      stepGranularity,
      hiddenLeafIds: [...hiddenMinorLeafIds],
    });
  }, [hiddenMinorLeafIds, onLibraryDisplayScope, stepGranularity]);

  const promotableLeaves = useMemo(() => {
    const seen = new Set<string>();
    const out: DrsLeafView[] = [];
    for (const { path, leaves: columnLeaves } of columns) {
      for (const step of resolveColumnSteps(path, columnLeaves, stepGranularity)) {
        const leaf = step.leaf;
        if (!leaf?.id || !String(leaf.question || '').trim() || seen.has(leaf.id)) continue;
        if (hiddenMinorLeafIds.has(leaf.id)) continue;
        seen.add(leaf.id);
        out.push(leaf);
      }
    }
    return out;
  }, [columns, hiddenMinorLeafIds, stepGranularity]);

  const pendingLeaves = useMemo(
    () => promotableLeaves.filter((leaf) => !pasPracticeLibraryHasLeaf(libraryItems, leaf)),
    [promotableLeaves, libraryItems],
  );

  const pendingSummary = useMemo(
    () => columns
      .map(({ candidate, path, leaves: columnLeaves }) => {
        const pending = resolveColumnSteps(path, columnLeaves, stepGranularity)
          .map((step) => step.leaf)
          .filter((leaf): leaf is DrsLeafView => Boolean(leaf?.id))
          .filter((leaf) => !pasPracticeLibraryHasLeaf(libraryItems, leaf));
        if (pending.length === 0) return null;
        return { label: kindLabelOf(candidate), count: pending.length };
      })
      .filter((item): item is { label: string; count: number } => item != null),
    [columns, libraryItems, stepGranularity],
  );

  const promote = async (nextLeaves: DrsLeafView[] = promotableLeaves) => {
    const proximityByLeafId = new Map<string, number>();
    for (const { path, leaves: columnLeaves } of columns) {
      for (const step of enrichStepsForDisplay(resolveColumnSteps(path, columnLeaves, stepGranularity))) {
        const id = String(step.leaf?.id || '').trim();
        if (id) proximityByLeafId.set(id, step.proximityLevel);
      }
    }
    const items = nextLeaves
      .filter((leaf) => String(leaf.question || '').trim())
      .filter((leaf) => !pasPracticeLibraryHasLeaf(libraryItems, leaf))
      .map((leaf) => {
        const override = leafProximityOverrides[leaf.id];
        const fromColumn = proximityByLeafId.get(leaf.id);
        const proximityLevel = override ?? fromColumn;
        return createPasPracticeLibraryItem({
          id: leaf.id,
          question: formatDrsStepQuestion(String(leaf.question || '').trim()),
          answer: String(leaf.answer || '').trim(),
          notes: leaf.hatchPrompt,
          practiceKind: leaf.practiceKind,
          pathId: leaf.pathId,
          transitionId: leaf.transitionId,
          entranceKind: leaf.entranceKind,
          treeRole: leaf.treeRole || 'leaf',
          implicitAbilityTagId: leaf.implicitAbilityTagId,
          eligibleForStream: true,
          reuseSource: 'generated',
          proximityLevel,
        });
      });
    if (items.length === 0) {
      if (nextLeaves.some((leaf) => String(leaf.question || '').trim())) {
        onToast?.('这些题已在练习库');
        return;
      }
      setError('还没有可加入的练习');
      return;
    }
    setPromoting(true);
    try {
      const ok = await onPromoteLeaves(items);
      if (ok) {
        onToast?.(`已加入 ${items.length} 道练习`);
      }
    } catch {
      onToast?.('加入练习库失败，请重试');
    } finally {
      setPromoting(false);
    }
  };

  const regenerateSameLevelStep = async (leaf: DrsLeafView, proximityLevel: number) => {
    const leafId = String(leaf.id || '').trim();
    if (!leafId || regeneratingLeafIds[leafId] || loading !== 'idle') return;
    setStepMenuLeafId(null);
    setRegeneratingLeafIds((prev) => ({ ...prev, [leafId]: true }));
    try {
      const avoid = Array.from(new Set([
        ...leaves.map((item) => String(item.question || '').trim()).filter(Boolean),
        String(leaf.question || '').trim(),
        ...listPasPracticeNegativeExampleQuestions(libraryItems),
      ].filter(Boolean)));
      const levelHint = proximityCaption(proximityLevel);
      const hatchPrompt = [
        `在「${levelHint}」同一难度级别，再出一道不同表述的阶梯练习变式。`,
        leaf.hatchPrompt ? `台阶提示：${leaf.hatchPrompt}` : '',
        leaf.question
          ? `参考题面（勿照搬）：${formatDrsStepQuestion(String(leaf.question)).slice(0, 280)}`
          : '',
      ].filter(Boolean).join('\n');

      const result = await generatePasPracticeDraftOne({
        assetId,
        regionId: regionId || undefined,
        sourceKpId: String(sourceKpId || resolveForestKpId(forest, sourceKpId) || '').trim() || null,
        hatchPrompt,
        avoidQuestions: avoid,
        negativeExamples: listPasPracticeNegativeExampleQuestions(libraryItems),
        subject,
        variantIndex: leaves.length + 1,
        existingCount: leaves.length,
      });
      if (!result.success || !result.draft?.question) {
        onToast?.(result.message || result.error || '同级练习生成失败');
        return;
      }
      const nextLeaf: DrsLeafView = {
        id: result.draft.id,
        question: result.draft.question,
        answer: result.draft.answer || '',
        hatchPrompt: result.hatchPromptUsed || leaf.hatchPrompt || hatchPrompt,
        practiceKind: leaf.practiceKind || 'transition',
        pathId: leaf.pathId,
        transitionId: leaf.transitionId,
        entranceKind: leaf.entranceKind,
        treeRole: 'leaf',
        implicitAbilityTagId: leaf.implicitAbilityTagId,
      };
      setLeafProximityOverrides((prev) => ({ ...prev, [nextLeaf.id]: proximityLevel }));
      setLeaves((prev) => {
        if (prev.some((item) => item.id === nextLeaf.id)) return prev;
        const index = prev.findIndex((item) => item.id === leafId);
        if (index < 0) return [...prev, nextLeaf];
        const next = [...prev];
        next.splice(index + 1, 0, nextLeaf);
        return next;
      });
      onToast?.('已追加一道同级练习（待加入）');
    } catch (regenError) {
      onToast?.(regenError instanceof Error ? regenError.message : '同级练习生成失败');
    } finally {
      setRegeneratingLeafIds((prev) => {
        const next = { ...prev };
        delete next[leafId];
        return next;
      });
    }
  };

  const removeStepLeaf = (leaf: DrsLeafView) => {
    const leafId = String(leaf.id || '').trim();
    if (!leafId) return;
    setStepMenuLeafId(null);
    setRemovedLeafIds((prev) => ({ ...prev, [leafId]: true }));
    setLeaves((prev) => prev.filter((item) => item.id !== leafId));
    if (pasPracticeLibraryHasLeaf(libraryItems, leaf)) {
      onRemoveLibraryItems?.([leafId]);
    }
    if (previewLeaf?.id === leafId) setPreviewLeaf(null);
    onToast?.('已删除该台阶练习');
  };

  const papReady = forestHasPapBlueprint(forest);
  const expandingAny = Object.keys(expandingIndexes).length > 0;
  const expandLocked = !papReady || loading !== 'idle';
  const shownReason = loading === 'idle' ? displayForestReason(forest?.reasonMarkdown) : '';
  const resolvedKpId = String(sourceKpId || resolveForestKpId(forest, sourceKpId) || '').trim();
  const originalQuestionDisplay = useMemo(() => (
    resolveKnowledgeParticleQaDisplay({
      questionSource: kpSourceParticle?.questions ?? forest?.evidencePack?.sourceQuestion,
      answerSource: kpSourceParticle?.answers,
      fallbackQuestionText: String(
        kpSourceParticle?.title || forest?.evidencePack?.sourceQuestion || '',
      ).trim() || undefined,
    }).questionDisplay
  ), [
    forest?.evidencePack?.sourceQuestion,
    kpSourceParticle?.answers,
    kpSourceParticle?.questions,
    kpSourceParticle?.title,
  ]);
  const hasOriginalQuestion = Boolean(
    originalQuestionDisplay.main || originalQuestionDisplay.items.length,
  );

  return (
    <div className="space-y-2 w-full min-w-0">
      <p className="text-sm font-semibold text-ink">阶梯练习</p>
      <p className="text-xs leading-relaxed text-muted">
        每张竖卡是一条解题路径。↑ 越靠上越难、越贴近原题；↓ 越向下越简单。已有阶梯会直接恢复；缺 PAP 或缺路径时才自动补齐。
      </p>
      {hasOriginalQuestion ? (
        <div className="space-y-2 rounded-xl border border-border bg-canvas px-3 py-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wide text-muted">原题</p>
          <KnowledgeParticleQuestionRichBody
            questionDisplay={originalQuestionDisplay}
            particleId={resolvedKpId}
            questionEmptyText="暂无原题正文"
          />
        </div>
      ) : kpSourceLoading || (resolvedKpId && !kpSourceParticle) ? (
        <div className="rounded-xl border border-dashed border-border bg-canvas/60 px-3 py-2.5 text-xs text-muted">
          正在加载原题正文…
        </div>
      ) : null}
      {candidates.length === 0 ? (
        <button
          type="button"
          disabled={loading !== 'idle' || expandingAny}
          onClick={() => void runEnumerate()}
          className="flex w-full items-center justify-center rounded-lg bg-ink px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {loading !== 'idle' ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin text-white" />
              {listActionLabel(loading)}
            </span>
          ) : '列出起手方式'}
        </button>
      ) : (
        <button
          type="button"
          disabled={loading !== 'idle' || expandingAny}
          onClick={() => void runEnumerate()}
          className="flex w-full items-center justify-center rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50"
        >
          {loading !== 'idle' ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-ink" />
              {listActionLabel(loading)}
            </span>
          ) : '重新分析起手方式'}
        </button>
      )}
      {loading === 'pap' ? (
        <p className="text-xs text-ink">{PAP_BACKGROUND_HINT}</p>
      ) : null}
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
      {shownReason ? <p className="text-xs text-ink">{shownReason}</p> : null}

      {columns.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted">
              {stepGranularity === 'major' ? '大台阶：合并细拆，快览关键跃迁' : '小台阶：逐步细拆'}
            </p>
            <div className="flex shrink-0 rounded-lg border border-border p-0.5">
              <button
                type="button"
                onClick={() => setStepGranularity('major')}
                className={
                  'rounded-md px-2.5 py-1 text-[11px] font-semibold transition '
                  + (stepGranularity === 'major'
                    ? 'bg-ink text-white'
                    : 'text-muted hover:text-ink')
                }
              >
                大台阶
              </button>
              <button
                type="button"
                onClick={() => setStepGranularity('minor')}
                className={
                  'rounded-md px-2.5 py-1 text-[11px] font-semibold transition '
                  + (stepGranularity === 'minor'
                    ? 'bg-ink text-white'
                    : 'text-muted hover:text-ink')
                }
              >
                小台阶
              </button>
            </div>
          </div>
          <div
            className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onScroll={(event) => {
              const node = event.currentTarget;
              const gap = 12;
              const columnWidth = (node.clientWidth - gap * 2) / 2.5;
              const step = columnWidth + gap;
              if (step <= 0) return;
              setFocusIndex(Math.round(node.scrollLeft / step));
            }}
          >
            {columns.map(({ candidate, path, leaves: columnLeaves }, columnIndex) => {
              const active = selected.includes(candidate.index);
              const sprouted = candidate.status === 'sprouted';
              const meta = candidateMeta(candidate);
              const kindLabel = kindLabelOf(candidate);
              const columnError = columnErrors[candidate.index] || candidate.expandError;
              const expanding = Boolean(expandingIndexes[candidate.index]);
              const columnSteps = resolveColumnSteps(path, columnLeaves, stepGranularity);
              const displaySteps = enrichStepsForDisplay(columnSteps)
                .filter((step) => !step.leaf?.id || !removedLeafIds[step.leaf.id]);
              return (
                <article
                  key={`${candidate.index}-${candidate.entranceKind}`}
                  className={
                    'w-[calc((100%-24px)/2.5)] shrink-0 snap-start rounded-xl border px-2 py-2.5 '
                    + (active ? 'border-ink bg-canvas' : 'border-border bg-surface')
                    + (sprouted ? '' : ' opacity-75')
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      toggleIndex(candidate.index);
                      setFocusIndex(columnIndex);
                    }}
                    className="flex w-full flex-col items-center text-center"
                  >
                    <span className={
                      'flex h-10 w-10 items-center justify-center rounded-full '
                      + (active ? 'bg-primary/15 text-primary-strong' : 'bg-canvas text-ink')
                    }
                    >
                      <Sprout size={22} strokeWidth={2.1} />
                    </span>
                    <p className="mt-2 text-[10px] font-bold text-muted">起手方式</p>
                    <p className="text-sm font-semibold text-ink">{kindLabel}</p>
                    {candidate.label && candidate.label !== kindLabel ? (
                      <p className="mt-0.5 text-xs font-medium text-ink">{candidate.label}</p>
                    ) : null}
                    <p className={
                      'text-[11px] font-bold '
                      + (columnError ? 'text-red-700' : 'text-primary-strong')
                    }
                    >
                      {columnError ? '展开失败' : statusLabel(candidate.status)}
                    </p>
                  {(path?.transitions || []).length > 0 || displaySteps.length > 0 ? (
                    <p className="mt-0.5 text-[10px] font-medium text-muted">↑ 越靠上越难 · ↓ 越向下越简单</p>
                  ) : null}
                    <p className="mt-1 text-xs leading-relaxed text-muted">{candidate.approachSummary}</p>
                    {meta ? <p className="mt-1 text-[11px] text-ink/70">{meta}</p> : null}
                  </button>

                  {displaySteps.map((step) => {
                    const leaf = step.leaf;
                    const hasQuestion = Boolean(leaf && String(leaf.question || '').trim());
                    const inLibrary = leaf ? pasPracticeLibraryHasLeaf(libraryItems, leaf) : false;
                    const regenerating = leaf ? Boolean(regeneratingLeafIds[leaf.id]) : false;
                    const menuOpen = leaf ? stepMenuLeafId === leaf.id : false;
                    return (
                    <div key={step.key} className="mt-2 flex flex-col items-center">
                      <span className="h-4 w-px bg-border" />
                      {hasQuestion && leaf ? (
                        <div
                          className={
                            'relative w-full rounded-lg border px-2.5 py-2 text-left transition '
                            + (inLibrary
                              ? 'border-primary/30 bg-primary/5'
                              : 'border-border hover:border-ink/40 hover:bg-canvas/60')
                          }
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setStepMenuLeafId(null);
                              setPreviewLeaf(leaf);
                              setPreviewProximityLevel(step.proximityLevel);
                            }}
                            className="w-full text-left"
                          >
                          <div className="flex w-full gap-2">
                            <StepDifficultyRail level={step.proximityLevel} />
                            <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 space-y-1">
                              <p className="text-[10px] font-bold text-ink">
                                {proximityCaption(step.proximityLevel)}
                                {step.op ? ` · ${transitionOpLabel(step.op)}` : ''}
                              </p>
                              <DifficultyMeter level={step.proximityLevel} />
                              {step.minorRange ? (
                                <p className="text-[10px] font-medium text-primary-strong">{step.minorRange}</p>
                              ) : null}
                            </div>
                            {inLibrary ? (
                              <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary-strong">
                                已加入
                              </span>
                            ) : (
                              <span className="shrink-0 rounded-full bg-canvas px-1.5 py-0.5 text-[10px] font-medium text-muted">
                                待加入
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 line-clamp-4 text-xs text-ink">
                            <MathTextBlock
                              text={formatDrsStepQuestion(String(leaf.question || ''))}
                              variant="document"
                            />
                          </div>
                          {step.summary ? (
                            <p className="mt-1 text-[10px] leading-relaxed text-muted line-clamp-2">{step.summary}</p>
                          ) : null}
                          <p className="mt-1 pr-8 text-[10px] font-medium text-ink/60">
                            {inLibrary ? '已加入练习库' : '点按预览 · 确认加入练习库时一并收录'}
                          </p>
                            </div>
                          </div>
                          </button>
                          <div className="absolute bottom-1.5 right-1.5" data-drs-step-menu>
                            <button
                              type="button"
                              disabled={regenerating || expandLocked}
                              onClick={(event) => {
                                event.stopPropagation();
                                setStepMenuLeafId((current) => (current === leaf.id ? null : leaf.id));
                              }}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted transition hover:bg-canvas hover:text-ink disabled:opacity-40"
                              aria-label="更多"
                              title="更多"
                            >
                              {regenerating
                                ? <Loader2 size={14} className="animate-spin" />
                                : <MoreVertical size={14} />}
                            </button>
                            {menuOpen ? (
                              <div className="absolute bottom-8 right-0 z-30 w-48 overflow-hidden rounded-lg border border-border bg-surface shadow-card">
                                <button
                                  type="button"
                                  disabled={regenerating || expandLocked}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void regenerateSameLevelStep(leaf, step.proximityLevel);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-ink hover:bg-canvas disabled:opacity-40"
                                >
                                  <RefreshCw size={13} className="shrink-0 text-muted" />
                                  再生成一道同级练习
                                </button>
                                <div className="mx-2 border-t border-border" />
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    removeStepLeaf(leaf);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-danger hover:bg-danger/5"
                                >
                                  <Trash2 size={13} className="shrink-0" />
                                  删除
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="flex w-full gap-2 rounded-lg border border-dashed border-border px-2.5 py-2">
                          <StepDifficultyRail level={step.proximityLevel} />
                          <div className="min-w-0 flex-1 text-left">
                          <p className="text-[10px] font-bold text-ink">
                            {proximityCaption(step.proximityLevel)}
                            {step.op ? ` · ${transitionOpLabel(step.op)}` : ''}
                          </p>
                          <div className="mt-1">
                            <DifficultyMeter level={step.proximityLevel} />
                          </div>
                          {step.summary ? (
                            <p className="mt-0.5 text-xs leading-relaxed text-muted">{step.summary}</p>
                          ) : null}
                          <p className="mt-1 text-[10px] font-medium text-amber-800">
                            点「{expandColumnActionLabel(columnSteps)}」生成此题
                          </p>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}

                  {columnError ? (
                    <p className="mt-2 text-center text-[11px] font-medium leading-relaxed text-red-700">
                      {columnError}
                    </p>
                  ) : null}

                  <div className="mt-2 w-full space-y-1">
                    <button
                      type="button"
                      disabled={expandLocked || expanding}
                      onClick={() => void runExpandColumn(candidate.index)}
                      className="flex w-full items-center justify-center rounded-lg border border-border px-2 py-1.5 text-[11px] font-semibold text-ink disabled:opacity-50"
                    >
                      {expanding ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Loader2 size={14} className="shrink-0 animate-spin" />
                          {expandColumnLoadingLabel(columnSteps)}
                        </span>
                      ) : expandColumnActionLabel(columnSteps)}
                    </button>
                    <p className="text-center text-[10px] leading-relaxed text-muted">
                      {expandColumnActionHint(columnSteps)}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
          {columns.length > 1 ? (
            <div className="flex justify-center gap-1.5">
              {columns.map((column, index) => (
                <span
                  key={column.candidate.index}
                  className={
                    'h-1.5 rounded-full '
                    + (index === focusIndex ? 'w-4 bg-ink' : 'w-1.5 bg-border')
                  }
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {promotableLeaves.length > 0 ? (
        <div className="space-y-2 rounded-xl border border-border bg-canvas/50 px-3 py-2.5">
          {pendingLeaves.length > 0 ? (
            <>
              <p className="text-xs font-semibold text-ink">
                将加入练习库 · {pendingLeaves.length} 道{stepGranularity === 'major' ? '大' : ''}台阶练习
              </p>
              <p className="text-[11px] leading-relaxed text-muted">
                {stepGranularity === 'major'
                  ? '当前为大台阶视图，仅收录合并后的关键台阶；细拆请切到小台阶。'
                  : '当前为小台阶视图，细拆台阶会一并收录。'}
                {' '}
                「已加入」的台阶自动跳过，不会重复收录。
                {pendingSummary.length > 0 ? (
                  <span className="mt-1 block text-ink/80">
                    {pendingSummary.map((item) => `${item.label} ${item.count} 道`).join(' · ')}
                  </span>
                ) : null}
              </p>
              <button
                type="button"
                disabled={promoting}
                onClick={() => void promote()}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {promoting ? <Loader2 size={14} className="animate-spin" /> : null}
                {promoting ? '保存中…' : `确认加入练习库（${pendingLeaves.length} 道）`}
              </button>
            </>
          ) : (
            <p className="text-center text-xs text-muted">本页台阶练习已全部在题目库中</p>
          )}
        </div>
      ) : null}

      {previewLeaf ? (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/40 p-4 sm:items-center"
          role="presentation"
          onClick={() => setPreviewLeaf(null)}
        >
          <div
            className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-surface p-4 shadow-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="drs-leaf-preview-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <p id="drs-leaf-preview-title" className="text-sm font-bold text-ink">
                {practiceKindLabel(previewLeaf.practiceKind)} · 练习详情
              </p>
              <button
                type="button"
                onClick={() => setPreviewLeaf(null)}
                className="shrink-0 text-xs font-semibold text-muted hover:text-ink"
              >
                关闭
              </button>
            </div>
            <div className="mt-3 space-y-2 text-sm text-ink">
              {previewProximityLevel > 0 ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-canvas px-2.5 py-1.5">
                  <span className="text-[11px] font-semibold text-ink">
                    {proximityCaption(previewProximityLevel)}
                  </span>
                  <DifficultyMeter level={previewProximityLevel} />
                </div>
              ) : null}
              <MathTextBlock text={formatDrsStepQuestion(String(previewLeaf.question || ''))} variant="document" />
            </div>
            {previewLeaf.answer ? (
              <div className="mt-3 text-sm text-ink">
                <p className="font-semibold">答：</p>
                <MathTextBlock text={String(previewLeaf.answer)} variant="document" className="mt-1" />
              </div>
            ) : null}
            {previewLeaf.hatchPrompt ? (
              <p className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-muted">
                <span className="font-semibold text-ink/70">出题意图：</span>
                {previewLeaf.hatchPrompt}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
